import * as THREE from 'three';
import {
  FilterKey,
  STADIUM_DATA as DATA,
  STATUS_COLORS,
  TvStatus,
} from './data';

// Three.js port of the lrb-sections.html prototype. Renders the stadium
// (sections 147/145, Nines Club, SMR suite, end-zone field boxes) and exposes
// hooks so React can drive live status colors, the section filter and the
// hover tooltip.

export type TvTip = {
  title: string;
  lines: string[];
  ip: string | null;
};

export type TvInfo = { sec: FilterKey; ip: string | null };

export type StadiumSceneHandle = {
  /** One entry per rendered TV — used by the HUD to compute status counts. */
  tvs: TvInfo[];
  updateStatuses(statusByIp: Map<string, TvStatus>): void;
  setFilter(filter: FilterKey): void;
  dispose(): void;
};

type TvUserData = {
  sec: FilterKey;
  ip: string | null;
  x: number;
  base: number;
  tip: TvTip;
  status: TvStatus;
};

type TvMesh = THREE.Mesh<THREE.BoxGeometry, THREE.MeshLambertMaterial>;

export function createStadiumScene(
  container: HTMLElement,
  callbacks: {
    onHover: (tip: TvTip | null, x: number, y: number) => void;
    /** Fired on click/tap: the hit TV's tip, or null for empty space. */
    onSelect: (tip: TvTip | null, x: number, y: number) => void;
  }
): StadiumSceneHandle {
  const { onHover, onSelect } = callbacks;
  /* ================= scene / camera ================= */
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b1116);
  scene.fog = new THREE.Fog(0x0b1116, 380, 820);

  const camera = new THREE.PerspectiveCamera(
    44,
    container.clientWidth / container.clientHeight,
    0.5,
    900
  );
  let theta = -Math.PI / 2,
    phi = Math.PI * 0.37,
    dist = 260;
  const target = new THREE.Vector3(0, 10, -8);
  function placeCamera() {
    phi = Math.max(0.1, Math.min(Math.PI * 0.47, phi));
    dist = Math.max(36, Math.min(320, dist));
    camera.position.set(
      target.x + dist * Math.sin(phi) * Math.cos(theta),
      target.y + dist * Math.cos(phi),
      target.z + dist * Math.sin(phi) * Math.sin(theta)
    );
    camera.lookAt(target);
  }
  placeCamera();

  /* ================= lights ================= */
  scene.add(new THREE.HemisphereLight(0xa5c8d6, 0x141c22, 0.9));
  const key = new THREE.DirectionalLight(0xffffff, 0.7);
  key.position.set(60, 100, 80);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0x00c8d2, 0.16);
  fill.position.set(-80, 40, -60);
  scene.add(fill);

  /* ================= materials ================= */
  const M = {
    carpet: new THREE.MeshLambertMaterial({ color: 0x1c2e35 }),
    carpetOff: new THREE.MeshLambertMaterial({ color: 0x161d22 }),
    stepFace: new THREE.MeshLambertMaterial({ color: 0x39464f }),
    podBase: new THREE.MeshLambertMaterial({ color: 0x2a3138 }),
    podBack: new THREE.MeshLambertMaterial({ color: 0x3a464f }),
    podOff: new THREE.MeshLambertMaterial({ color: 0x242b31 }),
    post: new THREE.MeshLambertMaterial({ color: 0x8f9ba3 }),
    turf: new THREE.MeshLambertMaterial({ color: 0x12653a }),
    wallPad: new THREE.MeshLambertMaterial({ color: 0x0e6f7a }),
    wallStripe: new THREE.MeshLambertMaterial({ color: 0xfc6a1e }),
    rail: new THREE.MeshLambertMaterial({ color: 0x5a6a74 }),
  };

  /* ================= layout =================
     x: across sections (sheet left -> right = 147 ... 145)
     +z: toward field (front). Rows terrace upward going -z. */
  const COL_W = 3.4,
    ROW_D = 6.6,
    RISE = 1.25,
    ROWS = 9,
    COLS = 9; /* col idx 4 = aisle */
  const SEC_W = COLS * COL_W;
  const SEC_GAP = 4.5;
  const FRONT_Z = 10;
  const ORDER: { key: 's147' | 's145' | null; label: string; tvs: boolean }[] =
    [
      { key: 's147', label: '147', tvs: true },
      { key: null, label: 'NO TVs', tvs: false },
      { key: null, label: 'NO TVs', tvs: false },
      { key: 's145', label: '145', tvs: true },
    ];
  const TOTAL_W = ORDER.length * SEC_W + (ORDER.length - 1) * SEC_GAP;
  const EZ_X = TOTAL_W / 2 + 64;
  function secX(i: number) {
    return TOTAL_W / 2 - SEC_W / 2 - i * (SEC_W + SEC_GAP);
  }

  /* ================= full field texture ================= */
  function fullFieldTexture() {
    const c = document.createElement('canvas');
    c.width = 4096;
    c.height = 2176;
    const g = c.getContext('2d')!;
    const cx = 2048,
      H = 2176,
      YD = 4096 / 100;

    g.fillStyle = '#12653a';
    g.fillRect(0, 0, 4096, H);
    for (let k = -10; k < 10; k++) {
      if ((k + 100) % 2 === 0) {
        g.fillStyle = '#0f5a34';
        g.fillRect(cx + k * 5 * YD, 0, 5 * YD, H);
      }
    }
    g.fillStyle = '#e8f2ec';
    g.fillRect(0, 0, 4096, 16);
    g.fillRect(0, H - 38, 4096, 16);
    g.fillStyle = '#0e6f7a';
    g.fillRect(0, H - 22, 4096, 22);

    g.fillStyle = '#e8f2ec';
    for (let k = -10; k <= 10; k++) {
      const x = cx + k * 5 * YD,
        w = Math.abs(k) === 10 ? 12 : 5;
      g.fillRect(x - w / 2, 16, w, H - 54);
    }
    for (let yd = -50; yd <= 50; yd++) {
      const x = cx + yd * YD;
      if (x < 8 || x > 4088) continue;
      g.fillRect(x - 1.5, H * 0.42, 3, 20);
      g.fillRect(x - 1.5, H * 0.56, 3, 20);
    }
    g.font = '700 96px Arial, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    function num(x: number, y: number, txt: string[]) {
      g.fillText(txt[0], x - 38, y);
      g.fillText(txt[1], x + 38, y);
    }
    function arrow(x: number, y: number, dir: number) {
      g.beginPath();
      g.moveTo(x + dir * 88, y);
      g.lineTo(x + dir * 64, y - 15);
      g.lineTo(x + dir * 64, y + 15);
      g.closePath();
      g.fill();
    }
    function numberRow(y: number) {
      num(cx, y, ['5', '0']);
      [10, 20, 30, 40].forEach((d) => {
        const t = String(50 - d).padStart(2, '0').split('');
        num(cx - d * YD, y, t);
        arrow(cx - d * YD, y, -1);
        num(cx + d * YD, y, t);
        arrow(cx + d * YD, y, +1);
      });
    }
    numberRow(H * 0.84);
    g.save();
    g.translate(cx, H * 0.16);
    g.rotate(Math.PI);
    g.translate(-cx, -H * 0.16);
    numberRow(H * 0.16);
    g.restore();

    g.strokeStyle = '#ff8a3c';
    g.lineWidth = 8;
    g.beginPath();
    g.arc(cx, H / 2, 110, 0, 7);
    g.stroke();

    const t = new THREE.CanvasTexture(c);
    t.anisotropy = 4;
    return t;
  }
  const TURF_W = 2 * (EZ_X - 5.2);
  const TURF_D = Math.round(TURF_W * 0.533);
  const turf = new THREE.Mesh(
    new THREE.PlaneGeometry(TURF_W, TURF_D),
    new THREE.MeshLambertMaterial({ map: fullFieldTexture() })
  );
  turf.rotation.x = -Math.PI / 2;
  turf.rotation.z = Math.PI;
  turf.position.set(0, 0, FRONT_Z + 3 + TURF_D / 2);
  scene.add(turf);
  const pad = new THREE.Mesh(
    new THREE.BoxGeometry(TOTAL_W + 50, 2.4, 1),
    M.wallPad
  );
  pad.position.set(0, 1.2, FRONT_Z + 2.8);
  scene.add(pad);
  const stripe = new THREE.Mesh(
    new THREE.BoxGeometry(TOTAL_W + 50, 0.45, 1.02),
    M.wallStripe
  );
  stripe.position.set(0, 1.75, FRONT_Z + 2.8);
  scene.add(stripe);

  /* ================= text sprites ================= */
  function textSprite(text: string, color: string, size: number) {
    const c = document.createElement('canvas');
    c.width = 512;
    c.height = 128;
    const g = c.getContext('2d')!;
    let f = 90;
    g.font = '700 ' + f + 'px ui-monospace, Menlo, monospace';
    const w = g.measureText(text).width;
    if (w > 484) f = Math.floor((f * 484) / w);
    g.font = '700 ' + f + 'px ui-monospace, Menlo, monospace';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = color;
    g.fillText(text, 256, 66);
    const t = new THREE.CanvasTexture(c);
    const s = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: t, transparent: true })
    );
    s.scale.set(size * 4, size, 1);
    return s;
  }

  /* ================= build sections ================= */
  const tvs: TvMesh[] = [];
  const podGeo = new THREE.BoxGeometry(COL_W - 0.8, 1.0, 2.6);
  const backGeo = new THREE.BoxGeometry(COL_W - 0.8, 1.3, 0.5);
  const screenGeo = new THREE.BoxGeometry(2.5, 1.5, 0.22);

  function makeTv(
    geo: THREE.BoxGeometry,
    sec: FilterKey,
    ip: string | null,
    x: number,
    tip: TvTip
  ): TvMesh {
    const tv = new THREE.Mesh(
      geo,
      new THREE.MeshLambertMaterial({
        color: 0x10181d,
        emissive: STATUS_COLORS.unknown,
        emissiveIntensity: 0.7,
      })
    );
    const u: TvUserData = { sec, ip, x, base: 0.7, tip, status: 'unknown' };
    tv.userData = u;
    tvs.push(tv);
    scene.add(tv);
    return tv;
  }

  ORDER.forEach((S, si) => {
    const sx = secX(si);
    const sec = S.key ? DATA[S.key] : null;

    for (let r = 0; r < ROWS; r++) {
      const y = r * RISE,
        zRow = FRONT_Z - r * ROW_D - ROW_D / 2;

      const floor = new THREE.Mesh(
        new THREE.BoxGeometry(SEC_W, 0.6, ROW_D),
        S.tvs ? M.carpet : M.carpetOff
      );
      floor.position.set(sx, y - 0.3, zRow);
      scene.add(floor);
      if (r > 0) {
        const face = new THREE.Mesh(
          new THREE.BoxGeometry(SEC_W, RISE, 0.4),
          M.stepFace
        );
        face.position.set(sx, y - RISE / 2, FRONT_Z - r * ROW_D + 0.2);
        scene.add(face);
      }
      const bar = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.07, SEC_W - 0.6, 6),
        M.rail
      );
      bar.rotation.z = Math.PI / 2;
      bar.position.set(sx, y + 1.5, FRONT_Z - r * ROW_D - 0.4);
      scene.add(bar);

      const zSeat = FRONT_Z - r * ROW_D - ROW_D + 2.6;
      for (let ci = 0; ci < COLS; ci++) {
        if (ci === 4) continue; /* center aisle */
        const px = sx + (4 - ci) * COL_W;
        const base = new THREE.Mesh(podGeo, S.tvs ? M.podBase : M.podOff);
        base.position.set(px, y + 0.5, zSeat);
        scene.add(base);
        const back = new THREE.Mesh(backGeo, S.tvs ? M.podBack : M.podOff);
        back.position.set(px, y + 1.35, zSeat - 1.15);
        scene.add(back);
      }

      if (sec) {
        const row = sec.rows[r];

        if (row.boxL !== null) {
          const sp = textSprite(String(row.boxL), '#7d95a3', 1.9);
          sp.position.set(sx + SEC_W / 2 + 1.8, y + 1.6, zSeat);
          scene.add(sp);
        }
        if (row.boxR !== null) {
          const sp = textSprite(String(row.boxR), '#7d95a3', 1.9);
          sp.position.set(sx - SEC_W / 2 - 1.8, y + 1.6, zSeat);
          scene.add(sp);
        }
        const rsp = textSprite('R' + (r + 1), '#4a565e', 1.3);
        rsp.position.set(sx, y + 2.8, FRONT_Z - r * ROW_D - 0.4);
        scene.add(rsp);

        row.tvs.forEach((t) => {
          const mid = (t.i0 + t.i1) / 2;
          const spansAisle = t.i0 < 4 && t.i1 > 4;
          const box = spansAisle
            ? row.boxL === row.boxR
              ? row.boxL
              : row.boxL + '/' + row.boxR
            : mid < 4
              ? row.boxL
              : row.boxR;
          const px = sx + (4 - mid) * COL_W;
          const zTV = FRONT_Z - r * ROW_D - 0.9;
          const post = new THREE.Mesh(
            new THREE.CylinderGeometry(0.08, 0.08, 1.7, 6),
            M.post
          );
          post.position.set(px, y + 2.0, zTV);
          scene.add(post);
          const dev = DATA.devices[S.label + '|' + box + '|' + t.num];
          const tv = makeTv(screenGeo, S.label as FilterKey, dev?.ip ?? null, px, {
            title: 'BOX ' + box + ' · TV ' + t.num,
            lines: [
              'SEC ' + S.label + ' · ROW ' + (r + 1) + ' of 9 (from field)',
              ...(dev
                ? [
                    dev.ip +
                      (dev.hd ? ' · ' + dev.hd : '') +
                      (dev.label ? ' · ' + dev.label : ''),
                  ]
                : []),
            ],
            ip: dev?.ip ?? null,
          });
          tv.position.set(px, y + 3.1, zTV);
          tv.rotation.x = 0.1;
        });
      }
    }

    const titleSp = textSprite(
      S.label,
      S.tvs ? '#00c8d2' : '#4a565e',
      S.tvs ? 3.4 : 2.2
    );
    titleSp.position.set(sx, ROWS * RISE + 2.3, FRONT_Z - ROWS * ROW_D - 0.7);
    scene.add(titleSp);
  });

  /* ================= field labels / compass ================= */
  const fieldSp = textSprite('NORTH SIDELINE · N', '#2fa06a', 4);
  fieldSp.position.set(0, 4, FRONT_Z + 3 + TURF_D + 12);
  scene.add(fieldSp);
  const westSp = textSprite('◀ WEST', '#9fb4c0', 4.5);
  westSp.position.set(EZ_X + 26, 6, FRONT_Z + 18);
  scene.add(westSp);
  const eastSp = textSprite('EAST ▶', '#9fb4c0', 4.5);
  eastSp.position.set(-EZ_X - 26, 6, FRONT_Z + 18);
  scene.add(eastSp);
  const southSp = textSprite('SOUTH SIDE', '#fc6a1e', 5);
  southSp.position.set(0, 8, FRONT_Z - ROWS * ROW_D - 42);
  scene.add(southSp);

  /* stadium logo marquee above the stands */
  let disposed = false;
  (function logoMarquee() {
    const ASPECT = 71.588 / 41.567;
    const W = 34,
      H = W / ASPECT;
    const img = new Image();
    img.onload = function () {
      if (disposed) return;
      const c = document.createElement('canvas');
      c.width = 1024;
      c.height = Math.round(1024 / ASPECT);
      c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
      const tex = new THREE.CanvasTexture(c);
      const sign = new THREE.Mesh(
        new THREE.PlaneGeometry(W, H),
        new THREE.MeshBasicMaterial({ map: tex, transparent: true })
      );
      sign.position.set(0, 42, FRONT_Z - ROWS * ROW_D - 38);
      scene.add(sign);
    };
    img.src = '/stadium-logo.svg';
  })();
  const marquee2 = textSprite('SOUTH SIDELINE · TV FLEET', '#00c8d2', 2.6);
  marquee2.position.set(0, 24, FRONT_Z - ROWS * ROW_D - 38);
  scene.add(marquee2);

  /* ================= Nines Club — upper deck spanning 147 -> 145 ================= */
  (function ninesClub() {
    const DECK_Y = ROWS * RISE + 4.5;
    const DECK_D = 13;
    const DECK_Z = FRONT_Z - ROWS * ROW_D - DECK_D / 2 - 1;
    const BOX_W = TOTAL_W / 9;
    const bigGeo = new THREE.BoxGeometry(4.6, 2.6, 0.28);

    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(TOTAL_W, 0.8, DECK_D),
      M.carpet
    );
    slab.position.set(0, DECK_Y - 0.4, DECK_Z);
    scene.add(slab);
    const face = new THREE.Mesh(
      new THREE.BoxGeometry(TOTAL_W, DECK_Y - ROWS * RISE + 1.5, 0.6),
      M.stepFace
    );
    face.position.set(0, (DECK_Y + ROWS * RISE - 1.5) / 2, DECK_Z + DECK_D / 2 - 0.3);
    scene.add(face);
    const wall = new THREE.Mesh(
      new THREE.BoxGeometry(TOTAL_W, 3.2, 0.7),
      M.stepFace
    );
    wall.position.set(0, DECK_Y + 1.6, DECK_Z - DECK_D / 2 - 0.3);
    scene.add(wall);

    const orderLR = [9, 7, 5, 3, 1, 2, 4, 6, 8];
    orderLR.forEach((nine, bi) => {
      const bx = TOTAL_W / 2 - BOX_W / 2 - bi * BOX_W;
      const frontZ = DECK_Z + DECK_D / 2;

      if (bi > 0) {
        const div = new THREE.Mesh(
          new THREE.BoxGeometry(0.4, 3.4, DECK_D - 1),
          M.podBack
        );
        div.position.set(bx + BOX_W / 2, DECK_Y + 1.7, DECK_Z);
        scene.add(div);
      }
      const glass = new THREE.Mesh(
        new THREE.PlaneGeometry(BOX_W - 1, 1.3),
        new THREE.MeshLambertMaterial({
          color: 0x9fd8e2,
          transparent: true,
          opacity: 0.22,
          side: THREE.DoubleSide,
        })
      );
      glass.position.set(bx, DECK_Y + 1.0, frontZ);
      scene.add(glass);
      const bar = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.07, BOX_W - 0.8, 6),
        M.rail
      );
      bar.rotation.z = Math.PI / 2;
      bar.position.set(bx, DECK_Y + 1.7, frontZ);
      scene.add(bar);

      (
        [
          [-2.2, -1.5],
          [2.2, -1.5],
          [-2.2, -5.5],
          [2.2, -5.5],
        ] as const
      ).forEach(([ox, oz]) => {
        const b = new THREE.Mesh(
          new THREE.BoxGeometry(3.4, 1.0, 2.2),
          M.podBase
        );
        b.position.set(bx + ox, DECK_Y + 0.5, frontZ + oz);
        scene.add(b);
      });

      const lbl = textSprite('9·' + nine, '#fc6a1e', 2.2);
      lbl.position.set(bx, DECK_Y + 4.6, frontZ - 1);
      scene.add(lbl);

      const spots = [
        { ox: -2.2, oz: -0.2, oy: 2.6, big: false },
        { ox: 2.2, oz: -0.2, oy: 2.6, big: false },
        { ox: -2.2, oz: -4.2, oy: 2.8, big: false },
        { ox: 2.2, oz: -4.2, oy: 2.8, big: false },
        { ox: 0, oz: -DECK_D + 2.2, oy: 3.6, big: true },
      ];
      spots.forEach((s, ti) => {
        const dev = DATA.devices['NINES|' + nine + '|' + (ti + 1)];
        if (!s.big) {
          const post = new THREE.Mesh(
            new THREE.CylinderGeometry(0.07, 0.07, 1.3, 6),
            M.post
          );
          post.position.set(bx + s.ox, DECK_Y + s.oy - 1.0, frontZ + s.oz);
          scene.add(post);
        }
        const tv = makeTv(s.big ? bigGeo : screenGeo, 'NINES', dev?.ip ?? null, bx + s.ox, {
          title: 'NINES CLUB · NINE ' + nine + ' · TV-' + (ti + 1),
          lines: dev ? [dev.ip + ' · ' + dev.hd + ' · ' + dev.name] : [],
          ip: dev?.ip ?? null,
        });
        tv.position.set(bx + s.ox, DECK_Y + s.oy, frontZ + s.oz);
        if (!s.big) tv.rotation.x = 0.1;
      });
    });

    const t = textSprite('NINES CLUB', '#00c8d2', 2.4);
    t.position.set(0, DECK_Y - 0.7, DECK_Z + DECK_D / 2 + 0.6);
    scene.add(t);
  })();

  /* ================= SMR suite — above the Nines ================= */
  (function smrSuite() {
    const NINES_Y = ROWS * RISE + 4.5;
    const NINES_D = 13;
    const NINES_Z = FRONT_Z - ROWS * ROW_D - NINES_D / 2 - 1;
    const NINES_REAR = NINES_Z - NINES_D / 2;
    const SMR_Y = NINES_Y + 3.5;
    const SMR_D = 11;
    const SMR_Z = NINES_REAR - SMR_D / 2 - 1.5;
    const SMR_W = 2 * SEC_W + SEC_GAP;

    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(SMR_W, 0.8, SMR_D),
      M.carpet
    );
    slab.position.set(0, SMR_Y - 0.4, SMR_Z);
    scene.add(slab);
    const face = new THREE.Mesh(
      new THREE.BoxGeometry(SMR_W, 3.2, 0.6),
      M.stepFace
    );
    face.position.set(0, SMR_Y - 2.0, SMR_Z + SMR_D / 2 - 0.3);
    scene.add(face);
    const wall = new THREE.Mesh(new THREE.BoxGeometry(SMR_W, 6, 0.7), M.stepFace);
    wall.position.set(0, SMR_Y + 3, SMR_Z - SMR_D / 2 - 0.3);
    scene.add(wall);
    const div = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 3.2, SMR_D - 1),
      M.podBack
    );
    div.position.set(0, SMR_Y + 1.6, SMR_Z);
    scene.add(div);

    const frontZ = SMR_Z + SMR_D / 2;
    const glass = new THREE.Mesh(
      new THREE.PlaneGeometry(SMR_W - 1, 1.3),
      new THREE.MeshLambertMaterial({
        color: 0x9fd8e2,
        transparent: true,
        opacity: 0.22,
        side: THREE.DoubleSide,
      })
    );
    glass.position.set(0, SMR_Y + 1.0, frontZ);
    scene.add(glass);
    const bar = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, SMR_W - 0.8, 6),
      M.rail
    );
    bar.rotation.z = Math.PI / 2;
    bar.position.set(0, SMR_Y + 1.7, frontZ);
    scene.add(bar);

    [-1, 1].forEach((sgn) => {
      for (let i = 0; i < 3; i++) {
        const b = new THREE.Mesh(new THREE.BoxGeometry(6, 1.0, 2.4), M.podBase);
        b.position.set(sgn * (6 + i * 9), SMR_Y + 0.5, SMR_Z - 2.2);
        scene.add(b);
      }
    });

    const SPACING = 5.4;
    const bankCenter = SMR_W / 4 + 1.2;
    for (let n = 1; n <= 10; n++) {
      const dev = DATA.devices['SMR|' + n];
      const replay = dev && /replay/i.test(dev.label);
      const bank147 = n <= 5;
      const k = bank147 ? n - 1 : n - 6;
      const cx = bank147 ? bankCenter : -bankCenter;
      const x = cx + (2 - k) * SPACING;
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.07, 1.3, 6),
        M.post
      );
      post.position.set(x, SMR_Y + 2.0, frontZ - 0.4);
      scene.add(post);
      const tv = makeTv(screenGeo, 'SMR', dev?.ip ?? null, x, {
        title: 'SMR SUITE · TV ' + n + (replay ? ' · REPLAY' : ''),
        lines: dev ? [dev.ip + ' · ' + dev.hd + ' · ' + dev.label] : [],
        ip: dev?.ip ?? null,
      });
      tv.position.set(x, SMR_Y + 3.0, frontZ - 0.4);
      tv.rotation.x = 0.1;
    }

    const t = textSprite('SMR SUITE', '#fc6a1e', 2.6);
    t.position.set(0, SMR_Y + 4.7, SMR_Z - SMR_D / 2 + 0.5);
    scene.add(t);
  })();

  /* ================= endzone Field Boxes ================= */
  (function endZones() {
    const BOX_D = 6.8,
      Z0 = 18,
      HALF_GAP = 5;
    const ezScreen = new THREE.BoxGeometry(2.5, 1.5, 0.22);

    type Zone = {
      x: number;
      face: number;
      name: string;
      startNorth: boolean;
      split: number;
      boxes: Record<number, { half: string; tvs: typeof DATA.ez }>;
      n: number;
    };
    const zones: Record<string, Zone> = {
      E: {
        x: -EZ_X,
        face: +1,
        name: 'EAST END ZONE',
        startNorth: true,
        split: 8,
        boxes: {},
        n: 0,
      },
      W: {
        x: EZ_X,
        face: -1,
        name: 'WEST END ZONE',
        startNorth: false,
        split: 10,
        boxes: {},
        n: 0,
      },
    };
    DATA.ez.forEach((e) => {
      const z = zones[e.half[1]];
      (z.boxes[e.box] = z.boxes[e.box] || { half: e.half, tvs: [] }).tvs.push(e);
    });
    Object.values(zones).forEach((z) => {
      z.n = Object.keys(z.boxes).length;
    });

    Object.values(zones).forEach((z) => {
      const len = z.n * BOX_D + HALF_GAP;
      const zOf = (b: number) =>
        z.startNorth
          ? Z0 + (z.n - b) * BOX_D + BOX_D / 2 + (b <= z.split ? HALF_GAP : 0)
          : Z0 + (b - 1) * BOX_D + BOX_D / 2 + (b > z.split ? HALF_GAP : 0);

      const FIELD_ZC = FRONT_Z + 3 + TURF_D / 2;
      const paint = new THREE.Mesh(
        new THREE.PlaneGeometry(16, TURF_D),
        new THREE.MeshLambertMaterial({ color: 0x0a4d63 })
      );
      paint.rotation.x = -Math.PI / 2;
      paint.position.set(z.x + z.face * 14, 0.01, FIELD_ZC);
      scene.add(paint);
      const endline = new THREE.Mesh(
        new THREE.PlaneGeometry(1.4, TURF_D),
        new THREE.MeshLambertMaterial({ color: 0xe8f2ec })
      );
      endline.rotation.x = -Math.PI / 2;
      endline.position.set(z.x + z.face * 5.2, 0.02, FIELD_ZC);
      scene.add(endline);

      const halves: Record<string, { lo: number; hi: number }> = {};
      Object.keys(z.boxes)
        .map(Number)
        .forEach((bn) => {
          const h = z.boxes[bn].half;
          const lo = zOf(bn) - BOX_D / 2,
            hi = zOf(bn) + BOX_D / 2;
          if (!halves[h]) halves[h] = { lo, hi };
          else {
            halves[h].lo = Math.min(halves[h].lo, lo);
            halves[h].hi = Math.max(halves[h].hi, hi);
          }
        });
      Object.values(halves).forEach((H) => {
        const slab = new THREE.Mesh(
          new THREE.BoxGeometry(6, 0.8, H.hi - H.lo),
          M.carpet
        );
        slab.position.set(z.x - z.face * 0.5, 0.4, (H.lo + H.hi) / 2);
        scene.add(slab);
      });

      let prevHalf: string | null = null;
      Object.keys(z.boxes)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((bn) => {
          const B = z.boxes[bn];
          const bz = zOf(bn);
          const base = new THREE.Mesh(
            new THREE.BoxGeometry(2.6, 1.0, BOX_D - 1.4),
            M.podBase
          );
          base.position.set(z.x, 1.3, bz);
          scene.add(base);
          const back = new THREE.Mesh(
            new THREE.BoxGeometry(0.5, 1.4, BOX_D - 1.4),
            M.podBack
          );
          back.position.set(z.x - z.face * 1.6, 1.6, bz);
          scene.add(back);
          const bl = textSprite(String(bn), '#7d95a3', 1.6);
          bl.position.set(z.x - z.face * 4.6, 2.2, bz);
          scene.add(bl);
          if (B.half !== prevHalf) {
            prevHalf = B.half;
            const hl = textSprite(B.half, '#fc6a1e', 2.6);
            hl.position.set(z.x - z.face * 4.6, 5.4, bz);
            scene.add(hl);
          }
          B.tvs
            .slice()
            .sort((a, b) => a.tv - b.tv)
            .forEach((e) => {
              const off =
                (e.tv === 1 ? 1 : -1) * (z.startNorth ? +1.55 : -1.55);
              const sx2 = z.x + z.face * 2.8,
                sz2 = bz + off;
              const post = new THREE.Mesh(
                new THREE.CylinderGeometry(0.07, 0.07, 1.6, 6),
                M.post
              );
              post.position.set(sx2, 1.6, sz2);
              scene.add(post);
              const tv = makeTv(ezScreen, 'EZ', e.ip, sx2, {
                title: e.half + ' FIELD BOX ' + e.box + ' · TV-' + e.tv,
                lines: [e.ip],
                ip: e.ip,
              });
              tv.position.set(sx2, 2.9, sz2);
              tv.rotation.y = Math.PI / 2;
            });
        });

      const t = textSprite(z.name, '#00c8d2', 3);
      t.position.set(z.x, 9, Z0 + len + 8);
      scene.add(t);
    });
  })();

  /* ================= filter / status ================= */
  let filter: FilterKey = 'ALL';

  function updateStatuses(statusByIp: Map<string, TvStatus>) {
    for (const tv of tvs) {
      const u = tv.userData as TvUserData;
      u.status = u.ip ? (statusByIp.get(u.ip) ?? 'unknown') : 'unknown';
      tv.material.emissive.setHex(STATUS_COLORS[u.status]);
    }
  }

  /* ================= interaction ================= */
  const ray = new THREE.Raycaster(),
    ptr = new THREE.Vector2();
  let dragging = false,
    panning = false,
    moved = false,
    lastX = 0,
    lastY = 0,
    autoRot = true,
    hovered: TvMesh | null = null;
  let touchCount = 0;
  const panVec = new THREE.Vector3();
  function panBy(dx: number, dy: number) {
    const s = dist * 0.0011;
    panVec.setFromMatrixColumn(camera.matrix, 0);
    target.addScaledVector(panVec, -dx * s);
    panVec.setFromMatrixColumn(camera.matrix, 1);
    target.addScaledVector(panVec, dy * s);
    target.x = Math.max(-EZ_X - 40, Math.min(EZ_X + 40, target.x));
    target.y = Math.max(0, Math.min(60, target.y));
    target.z = Math.max(-140, Math.min(FRONT_Z + 3 + TURF_D + 30, target.z));
    placeCamera();
  }

  function raycastTv(clientX: number, clientY: number): TvMesh | undefined {
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left,
      y = clientY - rect.top;
    ptr.x = (x / rect.width) * 2 - 1;
    ptr.y = -(y / rect.height) * 2 + 1;
    ray.setFromCamera(ptr, camera);
    const hit = ray.intersectObjects(tvs)[0];
    return (hit && hit.object) as TvMesh | undefined;
  }

  function pick(clientX: number, clientY: number) {
    const hitObj = raycastTv(clientX, clientY);
    if (hovered && hovered !== hitObj) {
      hovered.scale.set(1, 1, 1);
      hovered = null;
    }
    if (hitObj && !hovered) {
      hovered = hitObj;
      hovered.scale.set(1.35, 1.35, 1.35);
    }
    if (hitObj) {
      onHover((hitObj.userData as TvUserData).tip, clientX, clientY);
      container.style.cursor = 'pointer';
    } else {
      onHover(null, clientX, clientY);
      container.style.cursor = dragging ? 'grabbing' : 'grab';
    }
  }

  const onContextMenu = (e: Event) => e.preventDefault();
  const onPointerDown = (e: PointerEvent) => {
    dragging = true;
    moved = false;
    lastX = e.clientX;
    lastY = e.clientY;
    panning = e.button === 2 || e.shiftKey;
    autoRot = false;
  };
  const onPointerMove = (e: PointerEvent) => {
    if (dragging && touchCount < 2) {
      const dx = e.clientX - lastX,
        dy = e.clientY - lastY;
      if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
      if (panning) {
        panBy(dx, dy);
      } else {
        theta += dx * 0.0055;
        phi -= dy * 0.0045;
        placeCamera();
      }
      lastX = e.clientX;
      lastY = e.clientY;
    } else if (!dragging && e.pointerType !== 'touch') {
      pick(e.clientX, e.clientY);
    }
  };
  const onPointerUp = (e: PointerEvent) => {
    if (dragging && !moved && !panning) {
      pick(e.clientX, e.clientY);
      const hitObj = raycastTv(e.clientX, e.clientY);
      onSelect(
        hitObj ? (hitObj.userData as TvUserData).tip : null,
        e.clientX,
        e.clientY
      );
    }
    dragging = false;
    panning = false;
  };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    dist *= 1 + Math.sign(e.deltaY) * 0.08;
    placeCamera();
    autoRot = false;
  };

  let pinch0 = 0,
    midX = 0,
    midY = 0;
  const onTouchStart = (e: TouchEvent) => {
    touchCount = e.touches.length;
    if (e.touches.length === 2) {
      pinch0 = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
    }
  };
  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      const d = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      dist *= pinch0 / d;
      pinch0 = d;
      panBy(mx - midX, my - midY);
      midX = mx;
      midY = my;
      autoRot = false;
      e.preventDefault();
    }
  };
  const onTouchEnd = (e: TouchEvent) => {
    touchCount = e.touches.length;
  };

  container.addEventListener('contextmenu', onContextMenu);
  container.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  container.addEventListener('wheel', onWheel, { passive: false });
  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchmove', onTouchMove, { passive: false });
  container.addEventListener('touchend', onTouchEnd, { passive: true });

  const resizeObserver = new ResizeObserver(() => {
    const w = container.clientWidth,
      h = container.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  });
  resizeObserver.observe(container);

  /* ================= animate ================= */
  const clock = new THREE.Clock();
  let raf = 0;
  function loop() {
    raf = requestAnimationFrame(loop);
    const t = clock.getElapsedTime();
    if (autoRot) {
      theta += 0.001;
      placeCamera();
    }

    const span = 2 * EZ_X + 60;
    const sx = ((t * 16) % span) - span / 2;
    for (let i = 0; i < tvs.length; i++) {
      const tv = tvs[i],
        u = tv.userData as TvUserData;
      const dimmed = filter !== 'ALL' && u.sec !== filter;
      const boost = Math.max(0, 1 - Math.abs(u.x - sx) / 8);
      tv.material.emissiveIntensity = dimmed ? 0.06 : u.base + boost * 0.9;
    }
    renderer.render(scene, camera);
  }
  loop();

  return {
    tvs: tvs.map((tv) => {
      const u = tv.userData as TvUserData;
      return { sec: u.sec, ip: u.ip };
    }),
    updateStatuses,
    setFilter(f: FilterKey) {
      filter = f;
    },
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      container.removeEventListener('contextmenu', onContextMenu);
      container.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
